import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import RootLayout from "./RootLayout";
import { isAuthenticated } from "@/services/auth";

const HomePage = lazy(() => import("@/app/page"));
const DashboardPage = lazy(() => import("@/app/dashboard/page"));
const CheckInPage = lazy(() => import("@/app/check-in/page"));
const CheckOutPage = lazy(() => import("@/app/check-out/page"));
const ParkingListPage = lazy(() => import("@/app/parking-list/page"));
const VehicleDetailsPage = lazy(() => import("@/app/vehicle-details/[vehicleNumber]/page"));
const FinancePage = lazy(() => import("@/app/finance/page"));
const SettingsPage = lazy(() => import("@/app/settings/page"));
const AuditLogPage = lazy(() => import("@/app/audit-log/page"));
const TenantManagementPage = lazy(() => import("@/app/tenant-management/page"));
const TenantDetailPage = lazy(() => import("@/app/tenant-management/[id]/page"));
const SystemConfigPage = lazy(() => import("@/app/system-config/page"));
const CloakRoomPage = lazy(() => import("@/app/cloak-room/page"));
const ItemInPage = lazy(() => import("@/app/item-in/page"));
const ItemOutPage = lazy(() => import("@/app/item-out/page"));
const ItemListPage = lazy(() => import("@/app/item-list/page"));
const ItemDetailsPage = lazy(() => import("@/app/item-details/[tokenNumber]/page"));
const RoadTaxPage = lazy(() => import("@/app/road-tax/page"));
const TaxCollectionListPage = lazy(() => import("@/app/tax-collection-list/page"));
const UnauthorizedPage = lazy(() => import("@/app/unauthorized/page"));

function SuspensePage({ children }: { children: ReactNode }) {
  return <Suspense fallback={null}>{children}</Suspense>;
}

/** Mirrors `frontend/middleware.ts` protected routes (cookie-based on server; here `auth_user` / session). */
const PROTECTED_PREFIXES = [
  "/dashboard",
  "/check-in",
  "/check-out",
  "/parking-list",
  "/finance",
  "/audit-log",
  "/settings",
] as const;

function isProtectedPath(pathname: string) {
  return PROTECTED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function ProtectedRouteShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated() && isProtectedPath(location.pathname)) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  if (!isAuthenticated() && isProtectedPath(location.pathname)) {
    return null;
  }

  return <>{children}</>;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route
          index
          element={
            <SuspensePage>
              <HomePage />
            </SuspensePage>
          }
        />
        <Route
          path="unauthorized"
          element={
            <SuspensePage>
              <UnauthorizedPage />
            </SuspensePage>
          }
        />

        <Route
          path="dashboard"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <DashboardPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="check-in"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <CheckInPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="check-out"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <CheckOutPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="parking-list"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <ParkingListPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="finance"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <FinancePage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <SettingsPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />
        <Route
          path="audit-log"
          element={
            <ProtectedRouteShell>
              <SuspensePage>
                <AuditLogPage />
              </SuspensePage>
            </ProtectedRouteShell>
          }
        />

        <Route
          path="vehicle-details/:vehicleNumber"
          element={
            <SuspensePage>
              <VehicleDetailsPage />
            </SuspensePage>
          }
        />
        <Route
          path="tenant-management"
          element={
            <SuspensePage>
              <TenantManagementPage />
            </SuspensePage>
          }
        />
        <Route
          path="tenant-management/:id"
          element={
            <SuspensePage>
              <TenantDetailPage />
            </SuspensePage>
          }
        />
        <Route
          path="system-config"
          element={
            <SuspensePage>
              <SystemConfigPage />
            </SuspensePage>
          }
        />

        <Route
          path="cloak-room"
          element={
            <SuspensePage>
              <CloakRoomPage />
            </SuspensePage>
          }
        />
        <Route
          path="item-in"
          element={
            <SuspensePage>
              <ItemInPage />
            </SuspensePage>
          }
        />
        <Route
          path="item-out"
          element={
            <SuspensePage>
              <ItemOutPage />
            </SuspensePage>
          }
        />
        <Route
          path="item-list"
          element={
            <SuspensePage>
              <ItemListPage />
            </SuspensePage>
          }
        />
        <Route
          path="item-details/:tokenNumber"
          element={
            <SuspensePage>
              <ItemDetailsPage />
            </SuspensePage>
          }
        />

        <Route
          path="road-tax"
          element={
            <SuspensePage>
              <RoadTaxPage />
            </SuspensePage>
          }
        />
        <Route
          path="tax-collection-list"
          element={
            <SuspensePage>
              <TaxCollectionListPage />
            </SuspensePage>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
